/**
 * Downgrade is a blocker, not a confirmation, when usage is above the new
 * plan's limits — per design_handoff_billing. `blocked` picks between the
 * blocker table (nothing to confirm, just what to fix) and the plain
 * "usage already fits" confirmation that hands off to Stripe.
 *
 * The limit comparison stays client-side (product decision — see
 * helpers/subscription.js#isNewPlanIsBelowUsage), reusing the same
 * quotas/usage shape the existing Quota summary card already reads.
 */
import React, { useMemo } from "react";
import Modal from "../../theme/untitled/Modal/Modal";
import Button from "../../theme/untitled/Button/Button";
import Icon from "../../theme/untitled/icons/Icon";
import { QUOTAS, QuotaLabelMapping, UsageFieldsMapping } from "../../../../consts";
import { orderQuotas } from "../../helpers/subscription";
import styles from "./DowngradeModal.module.scss";

const ACTIONABLE = {
  [QUOTAS.widgets]: "Unpublish",
  [QUOTAS.team]: "Remove",
  [QUOTAS.integrations]: "Disconnect",
  [QUOTAS.domains]: "Remove",
};

const formatNumber = (n) =>
  Intl.NumberFormat(window.navigator.language || "en-US").format(n);

function buildRows(currentSubscription, currentOrganization, plan) {
  const quotas = currentSubscription.quotas || {};
  const usage = currentOrganization.model.usage || {};
  const newLimits = plan.metadata || {};

  const actionable = [];
  const reduced = [];

  orderQuotas(quotas, usage).forEach((key) => {
    const currentLimit = Number(quotas[key]);
    const newLimit = Number(newLimits[key]);
    if (!(newLimit >= 0)) return; // unlimited on the new plan — never a blocker
    const usageKey = UsageFieldsMapping[key];
    const used = usageKey ? Number(usage[usageKey]) || 0 : null;

    if (ACTIONABLE[key] && used !== null && used > newLimit) {
      actionable.push({
        key,
        label: QuotaLabelMapping[key],
        used,
        newLimit,
        action: `${ACTIONABLE[key]} ${formatNumber(used - newLimit)}`,
      });
    } else if (currentLimit >= 0 && newLimit < currentLimit) {
      reduced.push({ key, label: QuotaLabelMapping[key], used, newLimit });
    }
  });

  return { actionable, reduced };
}

const DowngradeModal = ({
  plan,
  price,
  blocked,
  currentOrganization,
  currentSubscription,
  onClose,
  onConfirm,
}) => {
  const { actionable, reduced } = useMemo(
    () => buildRows(currentSubscription, currentOrganization, plan),
    [currentSubscription, currentOrganization, plan]
  );

  if (blocked) {
    return (
      <Modal
        open
        onClose={onClose}
        size="lg"
        title={`You cannot downgrade to ${plan.name} yet`}
        footer={
          <>
            <span className={styles.note}>
              This is checked again next time, so the {plan.name} column keeps its
              Downgrade button.
            </span>
            <Button hierarchy="secondaryGray" onClick={onClose}>
              Close
            </Button>
          </>
        }
      >
        <p className={styles.lead}>
          Your usage is above the {plan.name} limits in {actionable.length}{" "}
          {actionable.length === 1 ? "place" : "places"}. Reduce{" "}
          {actionable.length === 1 ? "it" : "those"} and the downgrade becomes
          available — nothing is charged or changed until then.
        </p>
        <div className={styles.table}>
          <div className={styles.headerRow}>
            <span>Limit</span>
            <span>You use</span>
            <span>{plan.name} allows</span>
            <span>To downgrade</span>
          </div>
          {actionable.map((row) => (
            <div className={styles.row} key={row.key}>
              <span>{row.label}</span>
              <span>{formatNumber(row.used)}</span>
              <span>{formatNumber(row.newLimit)}</span>
              <b className={styles.over}>{row.action}</b>
            </div>
          ))}
          {reduced.map((row) => (
            <div className={styles.row} key={row.key}>
              <span>{row.label}</span>
              <span>{row.used !== null ? formatNumber(row.used) : "—"}</span>
              <span>{formatNumber(row.newLimit)}</span>
              <span className={styles.same}>Reduced</span>
            </div>
          ))}
        </div>
        {reduced.length > 0 && (
          <p className={styles.footnote}>
            {reduced.length === 1 ? "The other limit is" : "The other limits are"}{" "}
            simply lower on {plan.name}. {reduced.length === 1 ? "It reduces" : "They reduce"}{" "}
            on its own, so there is nothing to do about {reduced.length === 1 ? "it" : "them"}.
          </p>
        )}
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      icon={<Icon name="ArrowDown" size={24} />}
      title={`Downgrade to ${plan.name}?`}
      footer={
        <>
          <Button hierarchy="secondaryGray" onClick={onClose}>
            Keep current plan
          </Button>
          <Button
            hierarchy="primary"
            iconTrailing={<Icon name="ArrowUpRight" size={14} />}
            onClick={onConfirm}
          >
            Continue in Stripe
          </Button>
        </>
      }
    >
      <p className={styles.lead}>
        Your usage already fits inside the {plan.name} limits, so nothing will be
        blocked. The change takes effect when the current billing period ends, and
        Stripe handles the billing.
      </p>
    </Modal>
  );
};

export default DowngradeModal;
